import { create } from 'zustand'
import type { LocaleCode } from '@/i18n/locales'
import { DEFAULT_LOCALE, isLocaleCode } from '@/i18n/locales'
import { load, save } from '@/lib/storage'
import type { StoreDefinition } from '@/lib/storage'
import { DEFAULT_PERSONA, isPersonaId } from '@/persona/personas'
import type { PersonaId } from '@/persona/personas'

/**
 * Everything the person can change about how Namu behaves.
 *
 * The patience scale is the important one and it is not a nicety. The silence
 * Namu waits through before deciding a turn has ended is a time limit set by
 * the content, which accessibility guidance requires to be adjustable across
 * at least ten times the default. It also has real evidence behind it: raising
 * the endpointing threshold enough to stop cutting off people who stutter cost
 * about 1.2 seconds of median latency and removed roughly four fifths of the
 * truncations. That trade is the user's to make, not ours.
 */

export type Patience = 'quick' | 'normal' | 'patient' | 'veryPatient'

/** Milliseconds of silence that end a turn. Spans 12x, comfortably past 10x. */
export const PATIENCE_MS: Record<Patience, number> = {
  quick: 500,
  normal: 900,
  patient: 2500,
  veryPatient: 6000,
}

/**
 * How long to wait for someone to start speaking at all.
 *
 * Distinct from the end-of-turn silence above, and it has to scale with the
 * same setting. A fixed nine seconds meant the patience control only helped
 * people who had already begun: somebody who needs eleven seconds to get their
 * first word out was cut off by "Are you still there?" three times running and
 * the question was abandoned. Reported cases of sound repetition on an initial
 * word run to fifteen seconds, which is what the longest setting is sized for.
 */
export const START_TIMEOUT_MS: Record<Patience, number> = {
  quick: 6000,
  normal: 9000,
  patient: 16000,
  veryPatient: 28000,
}

export interface ListenTimings {
  endSilenceMs: number
  startTimeoutMs: number
}

export const PATIENCE_ORDER: readonly Patience[] = ['quick', 'normal', 'patient', 'veryPatient']

export type Appearance = 'system' | 'light' | 'dark'

export interface Settings {
  locale: LocaleCode
  /** Whether the person explicitly chose a language, versus it being detected. */
  localeChosen: boolean
  captionsAlwaysOn: boolean
  /**
   * Which persona answers. Travels to the server on every session, because the
   * device is the only place the choice is recorded.
   */
  persona: PersonaId
  patience: Patience
  /** Speaking rate multiplier, 0.6 to 1.6. */
  rate: number
  haptics: boolean
  appearance: Appearance
  /** Overrides the OS reduced-motion preference when the user turns it on. */
  reduceMotion: boolean
}

function defaultSettings(): Settings {
  return {
    // Hausa, not the device's language. The reasoning is in
    // `src/i18n/locales.ts`, and `localeChosen` staying false is the half of
    // it that matters here: this is an opening position, not a choice made on
    // somebody's behalf, so the first reply or one row in Settings replaces it
    // without argument.
    locale: DEFAULT_LOCALE,
    localeChosen: false,
    // Off by default. The conversation happens out loud, and a running
    // transcript invites people to read rather than talk. The one case that
    // overrides this is a language with no synthesised voice, where the screen
    // is the only channel left; the conversation screen forces captions on for
    // those regardless of this setting.
    captionsAlwaysOn: false,
    persona: DEFAULT_PERSONA,
    patience: 'normal',
    rate: 1,
    haptics: true,
    // Light by default, not "match device". Harmattan cream is the brand's
    // primary surface, and a phone left in dark mode should not decide that
    // for a first-time user. "Match device" is still one tap away in settings.
    appearance: 'light',
    reduceMotion: false,
  }
}

const DEFINITION: StoreDefinition<Settings> = {
  key: 'settings',
  version: 2,
  fallback: defaultSettings,
  /**
   * Version 2 added the persona. Everything already on a phone was written
   * without one, and `load` hands back exactly what it read, so without this
   * an existing user would boot with `persona` undefined and send nothing to
   * the server. Filling gaps from the defaults rather than discarding the
   * record keeps somebody's language and appearance through the upgrade.
   */
  migrate: (data) => {
    if (typeof data !== 'object' || data === null) return data
    return { ...defaultSettings(), ...data }
  },
  validate: (data): data is Settings => {
    if (typeof data !== 'object' || data === null) return false
    const value = data as Partial<Settings>
    return (
      isLocaleCode(value.locale) &&
      typeof value.captionsAlwaysOn === 'boolean' &&
      typeof value.rate === 'number' &&
      isPersonaId(value.persona) &&
      value.patience !== undefined &&
      PATIENCE_ORDER.includes(value.patience)
    )
  },
}

interface SettingsState extends Settings {
  set<K extends keyof Settings>(key: K, value: Settings[K]): void
  chooseLocale(locale: LocaleCode): void
  reset(): void
  /** Silence that ends a turn, from the patience setting. */
  endSilenceMs(): number
  /** Both listening timings, so no caller can set one and forget the other. */
  timings(): ListenTimings
}

export const useSettingsStore = create<SettingsState>()((setState, getState) => {
  const persist = () => {
    const { set: _set, chooseLocale: _choose, reset: _reset, endSilenceMs: _ms, ...settings } = getState()
    save(DEFINITION, settings)
  }

  return {
    ...load(DEFINITION),

    set(key, value) {
      setState({ [key]: value } as Partial<SettingsState>)
      persist()
    },

    chooseLocale(locale) {
      // Marking the choice as explicit stops a later browser-language change
      // from silently switching the language out from under someone.
      setState({ locale, localeChosen: true })
      persist()
    },

    reset() {
      setState(defaultSettings())
      persist()
    },

    endSilenceMs() {
      return PATIENCE_MS[getState().patience]
    },

    timings() {
      const { patience } = getState()
      return {
        endSilenceMs: PATIENCE_MS[patience],
        startTimeoutMs: START_TIMEOUT_MS[patience],
      }
    },
  }
})

/** Reads settings outside React, for the transports and the director. */
export function readSettings(): Settings {
  const {
    set: _set,
    chooseLocale: _choose,
    reset: _reset,
    endSilenceMs: _ms,
    timings: _timings,
    ...settings
  } = useSettingsStore.getState()
  return settings
}
